import type { FxAudioFeatures } from "./fxRuntime";

type RuntimeMediaMaps = {
  audioMap: Map<string, HTMLAudioElement>;
  videoMap: Map<string, HTMLVideoElement>;
};

type SourceBundle = {
  media: HTMLMediaElement;
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  data: Uint8Array;
  stream: MediaStream;
};

const DEFAULT_FEATURES: FxAudioFeatures = {
  low: 0,
  mid: 0,
  high: 0,
  full: 0,
  onset: 0,
  transient: 0,
  beat: 0,
  tempoBpm: 0,
};

const ANALYSER_FFT_SIZE = 1024;
const ANALYSER_SMOOTHING = 0.72;
const ATTACK_SMOOTH = 0.48;
const RELEASE_SMOOTH = 0.26;
const BEAT_DECAY = 0.72;
const BEAT_REFRACTORY_MS = 180;
const TEMPO_MIN_BPM = 48;
const TEMPO_MAX_BPM = 180;

export class FxAudioFeatureAnalyzer {
  private audioContext: AudioContext | null = null;
  private bundles = new Map<string, SourceBundle>();
  private cachedFeatures: FxAudioFeatures = { ...DEFAULT_FEATURES };
  private prevSpectrum: Float32Array | null = null;
  private weightedSpectrum: Float32Array | null = null;
  private fluxEma = 0;
  private prevFull = 0;
  private beatPulse = 0;
  private lastBeatAtMs = 0;
  private beatIntervalEmaMs = 500;

  sample(runtime: RuntimeMediaMaps): FxAudioFeatures {
    const context = this.ensureAudioContext();
    if (!context) {
      this.cachedFeatures = smoothFeatures(this.cachedFeatures, DEFAULT_FEATURES);
      return this.cachedFeatures;
    }

    this.syncBundles(runtime, context);
    if (this.bundles.size === 0) {
      this.cachedFeatures = smoothFeatures(this.cachedFeatures, DEFAULT_FEATURES);
      return this.cachedFeatures;
    }

    let weightSum = 0;
    let lowSum = 0;
    let midSum = 0;
    let highSum = 0;
    let fullSum = 0;

    for (const bundle of this.bundles.values()) {
      const media = bundle.media;
      if (media.paused || media.ended || media.muted) continue;

      const weight = clamp01(Number(media.volume) || 0);
      if (weight <= 0.001) continue;

      bundle.analyser.getByteFrequencyData(bundle.data);
      const features = computeBands(bundle.data, context.sampleRate);

      weightSum += weight;
      lowSum += features.low * weight;
      midSum += features.mid * weight;
      highSum += features.high * weight;
      fullSum += features.full * weight;

      const dataLength = bundle.data.length;
      if (!this.weightedSpectrum || this.weightedSpectrum.length !== dataLength) {
        this.weightedSpectrum = new Float32Array(dataLength);
      } else {
        this.weightedSpectrum.fill(0);
      }
      for (let i = 0; i < dataLength; i += 1) {
        this.weightedSpectrum[i] += (bundle.data[i] / 255) * weight;
      }
    }

    const targetBands =
      weightSum > 0.0001
        ? {
            low: clamp01(lowSum / weightSum),
            mid: clamp01(midSum / weightSum),
            high: clamp01(highSum / weightSum),
            full: clamp01(fullSum / weightSum),
          }
        : {
            low: 0,
            mid: 0,
            high: 0,
            full: 0,
          };

    const nowMs = performance.now();
    const reactive = this.computeReactiveSignals(targetBands, this.weightedSpectrum, weightSum, nowMs);

    const target: FxAudioFeatures = {
      ...targetBands,
      onset: reactive.onset,
      transient: reactive.transient,
      beat: reactive.beat,
      tempoBpm: reactive.tempoBpm,
    };

    this.cachedFeatures = smoothFeatures(this.cachedFeatures, target);
    return this.cachedFeatures;
  }

  dispose(): void {
    for (const key of this.bundles.keys()) {
      this.destroyBundle(key);
    }
    this.bundles.clear();
    this.failedKeys.clear();

    if (this.audioContext) {
      void this.audioContext.close().catch(() => undefined);
      this.audioContext = null;
    }

    this.prevSpectrum = null;
    this.weightedSpectrum = null;
    this.fluxEma = 0;
    this.prevFull = 0;
    this.beatPulse = 0;
    this.lastBeatAtMs = 0;
    this.beatIntervalEmaMs = 500;
    this.cachedFeatures = { ...DEFAULT_FEATURES };
  }

  private computeReactiveSignals(
    targetBands: { low: number; mid: number; high: number; full: number },
    weightedSpectrum: Float32Array | null,
    weightSum: number,
    nowMs: number
  ): Pick<FxAudioFeatures, "onset" | "transient" | "beat" | "tempoBpm"> {
    if (!weightedSpectrum || weightSum <= 0.0001) {
      this.prevFull = lerp(this.prevFull, 0, 0.24);
      this.beatPulse *= BEAT_DECAY;
      this.fluxEma = lerp(this.fluxEma, 0, 0.2);
      return {
        onset: 0,
        transient: 0,
        beat: clamp01(this.beatPulse),
        tempoBpm: 0,
      };
    }

    for (let i = 0; i < weightedSpectrum.length; i += 1) {
      weightedSpectrum[i] /= weightSum;
    }

    const flux = computeSpectralFlux(weightedSpectrum, this.prevSpectrum);
    this.prevSpectrum = copySpectrum(weightedSpectrum, this.prevSpectrum);

    this.fluxEma = lerp(this.fluxEma, flux, 0.15);
    const fluxSpike = clamp01((flux - this.fluxEma * 0.92) * 6.4);

    const fullDelta = Math.max(0, targetBands.full - this.prevFull);
    this.prevFull = targetBands.full;

    const transient = clamp01(fullDelta * 3.6 + fluxSpike * 0.4);
    const onset = clamp01(fluxSpike * 0.75 + transient * 0.25);

    const beatCandidate = clamp01(targetBands.low * 0.72 + onset * 0.28);
    const beatThreshold = clamp(0.22 + this.fluxEma * 0.75, 0.24, 0.85);
    const canTriggerBeat = nowMs - this.lastBeatAtMs >= BEAT_REFRACTORY_MS;

    if (canTriggerBeat && beatCandidate >= beatThreshold) {
      if (this.lastBeatAtMs > 0) {
        const beatIntervalMs = nowMs - this.lastBeatAtMs;
        this.beatIntervalEmaMs = lerp(this.beatIntervalEmaMs, beatIntervalMs, 0.25);
      }
      this.lastBeatAtMs = nowMs;
      this.beatPulse = 1;
    } else {
      this.beatPulse *= BEAT_DECAY;
    }

    const beat = clamp01(Math.max(this.beatPulse, beatCandidate * 0.22));
    const tempoBpm =
      this.lastBeatAtMs > 0 && this.beatIntervalEmaMs > 0
        ? clamp(60000 / this.beatIntervalEmaMs, TEMPO_MIN_BPM, TEMPO_MAX_BPM)
        : 0;

    return {
      onset,
      transient,
      beat,
      tempoBpm,
    };
  }

  private ensureAudioContext(): AudioContext | null {
    if (typeof window === "undefined") return null;
    if (this.audioContext) {
      if (this.audioContext.state === "suspended") {
        void this.audioContext.resume().catch(() => undefined);
      }
      return this.audioContext;
    }

    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (typeof Ctor !== "function") return null;

    try {
      const ctx = new Ctor() as AudioContext;
      this.audioContext = ctx;
      if (ctx.state === "suspended") {
        void ctx.resume().catch(() => undefined);
      }
      return ctx;
    } catch {
      this.audioContext = null;
      return null;
    }
  }

  private syncBundles(runtime: RuntimeMediaMaps, context: AudioContext): void {
    const targetKeys = new Set<string>();
    for (const [id, audio] of runtime.audioMap.entries()) {
      const key = `audio:${id}`;
      targetKeys.add(key);
      // Clear failed state when media element changes (re-hydration)
      const existing = this.bundles.get(key);
      if (existing && existing.media !== audio) this.failedKeys.delete(key);
      this.ensureBundle(key, audio, context);
    }
    for (const [id, video] of runtime.videoMap.entries()) {
      const key = `video:${id}`;
      targetKeys.add(key);
      const existing = this.bundles.get(key);
      if (existing && existing.media !== video) this.failedKeys.delete(key);
      this.ensureBundle(key, video, context);
    }

    for (const key of this.bundles.keys()) {
      if (!targetKeys.has(key)) {
        this.destroyBundle(key);
        this.failedKeys.delete(key);
      }
    }
  }

  /** Keys that failed captureStream — skip retrying until the media element changes */
  private failedKeys = new Set<string>();

  private ensureBundle(key: string, media: HTMLMediaElement, context: AudioContext): void {
    const existing = this.bundles.get(key);
    if (existing?.media === media) return;
    if (existing) this.destroyBundle(key);

    // Don't retry captureStream on media that already failed (prevents infinite runtime.lastError).
    // Reset when the media element reference changes (re-hydration).
    if (this.failedKeys.has(key)) return;

    // Guard: skip videos that aren't loaded enough for captureStream.
    // Calling captureStream() on unloaded/deferred videos triggers Chrome's internal
    // runtime.lastError ("Could not establish connection") every frame.
    if (media.readyState < 2) return; // Need at least HAVE_CURRENT_DATA

    const stream = getMediaAudioStream(media);
    if (!stream || stream.getAudioTracks().length === 0) {
      // Mark as failed so we don't re-attempt captureStream every frame
      this.failedKeys.add(key);
      return;
    }

    try {
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = ANALYSER_FFT_SIZE;
      analyser.smoothingTimeConstant = ANALYSER_SMOOTHING;
      source.connect(analyser);

      this.bundles.set(key, {
        media,
        source,
        analyser,
        data: new Uint8Array(analyser.frequencyBinCount),
        stream,
      });
    } catch {
      stopStreamTracks(stream);
      this.failedKeys.add(key);
    }
  }

  private destroyBundle(key: string): void {
    const bundle = this.bundles.get(key);
    if (!bundle) return;
    try {
      bundle.source.disconnect();
    } catch {}
    try {
      bundle.analyser.disconnect();
    } catch {}
    stopStreamTracks(bundle.stream);
    this.bundles.delete(key);
  }
}

function computeBands(data: Uint8Array, sampleRate: number): Pick<FxAudioFeatures, "low" | "mid" | "high" | "full"> {
  if (!data.length || sampleRate <= 0) return { low: 0, mid: 0, high: 0, full: 0 };
  const binHz = (sampleRate / 2) / data.length;

  const low = rmsForRange(data, binHz, 20, 250);
  const mid = rmsForRange(data, binHz, 250, 2000);
  const high = rmsForRange(data, binHz, 2000, 8000);
  const full = rmsForRange(data, binHz, 20, 8000);
  return { low, mid, high, full };
}

function rmsForRange(data: Uint8Array, binHz: number, minHz: number, maxHz: number): number {
  const start = Math.max(0, Math.floor(minHz / Math.max(binHz, 1)));
  const end = Math.min(data.length - 1, Math.ceil(maxHz / Math.max(binHz, 1)));
  if (end < start) return 0;

  let count = 0;
  let sumSquares = 0;
  for (let i = start; i <= end; i += 1) {
    const n = data[i] / 255;
    sumSquares += n * n;
    count += 1;
  }
  if (count <= 0) return 0;
  return clamp01(Math.sqrt(sumSquares / count));
}

function computeSpectralFlux(current: Float32Array, previous: Float32Array | null): number {
  if (!previous || previous.length !== current.length) return 0;
  let flux = 0;
  for (let i = 0; i < current.length; i += 1) {
    const diff = current[i] - previous[i];
    if (diff > 0) flux += diff;
  }
  return clamp01(flux / Math.max(1, current.length) * 3.2);
}

function copySpectrum(source: Float32Array, target: Float32Array | null): Float32Array {
  if (!target || target.length !== source.length) {
    return new Float32Array(source);
  }
  target.set(source);
  return target;
}

function smoothFeatures(prev: FxAudioFeatures, next: FxAudioFeatures): FxAudioFeatures {
  return {
    low: smoothValue(prev.low, next.low),
    mid: smoothValue(prev.mid, next.mid),
    high: smoothValue(prev.high, next.high),
    full: smoothValue(prev.full, next.full),
    onset: smoothReactiveValue(prev.onset, next.onset),
    transient: smoothReactiveValue(prev.transient, next.transient),
    beat: clamp01(Math.max(next.beat, prev.beat * BEAT_DECAY)),
    tempoBpm: next.tempoBpm > 0 ? lerp(prev.tempoBpm, next.tempoBpm, 0.35) : lerp(prev.tempoBpm, 0, 0.25),
  };
}

function smoothValue(prev: number, next: number): number {
  const alpha = next > prev ? ATTACK_SMOOTH : RELEASE_SMOOTH;
  return prev + (next - prev) * alpha;
}

function smoothReactiveValue(prev: number, next: number): number {
  const attack = 0.62;
  const release = 0.2;
  const alpha = next > prev ? attack : release;
  return prev + (next - prev) * alpha;
}

function getMediaAudioStream(media: HTMLMediaElement): MediaStream | null {
  // Guard: captureStream on unloaded or cross-origin media triggers Chrome's
  // internal runtime.lastError ("Could not establish connection") that cannot be
  // caught by try/catch and floods the console.
  if (media.readyState < 2) return null; // HAVE_CURRENT_DATA minimum
  if (!media.src && !(media as any).srcObject) return null; // No source loaded

  const anyMedia = media as any;
  try {
    if (typeof anyMedia.captureStream === "function") return anyMedia.captureStream();
  } catch {}
  try {
    if (typeof anyMedia.mozCaptureStream === "function") return anyMedia.mozCaptureStream();
  } catch {}
  return null;
}

function stopStreamTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {}
  }
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}
